// components/Teams/TeamFormModal.js
import React, { useState, useEffect } from "react";

const TEAM_TAXONOMY = {
  office: {
    label: "🏢 Office / Corporate",
    flow: "workforce",
    types: {
      general_office: ["admin", "coordinator", "documentation", "scheduling", "office_assistant"],
      technology: ["software_engineering", "ai_ml", "data_science", "cybersecurity", "devops", "it_support"],
      operations: ["business_operations", "program_management", "process_management", "supply_chain", "internal_workflow"],
      product_strategy: ["product_manager", "ux_ui", "product_design", "strategy", "innovation"],
      sales_marketing: ["sales", "customer_success", "marketing", "growth", "partnerships"],
      finance_analytics: ["accounting", "financial_analyst", "risk_analyst", "business_intelligence", "audit"],
      human_resources: ["recruiting", "talent_acquisition", "employee_relations", "training", "hr_operations"],
      research_innovation: ["ai_research", "behavioral_research", "product_research", "market_research", "innovation_lab"],
      executive_office: ["executive_assistant", "director", "vp", "c_level", "leadership"]
    }
  },
  candidate: {
    label: "👥 Candidate Hiring",
    flow: "hiring",
    types: {
      corporate_candidate: ["entry_level", "manager", "technical", "sales", "operations"],
      bank_candidate: ["retail_banking", "compliance", "risk", "fraud", "wealth_management"],
      retail_candidate: ["store_associate", "inventory", "customer_service", "fulfillment", "shift_lead"],
      technical_candidate: ["software_engineer", "ai_engineer", "data_analyst", "cybersecurity", "devops"]
    }
  },
  employee: {
    label: "👔 Employee Workforce",
    flow: "workforce",
    types: {
      performance_review: ["thirty_day", "sixty_day", "ninety_day", "annual_review"],
      burnout_monitoring: ["stress_recovery", "sleep_impact", "workload_sustainability", "fatigue_risk"],
      promotion_readiness: ["leadership_track", "manager_readiness", "executive_potential", "high_performer"],
      department_fit: ["reassignment", "internal_mobility", "role_optimization", "team_compatibility"]
    }
  },
  student: {
    label: "🎓 Student Career / College",
    flow: "career_college",
    types: {
      career_discovery: ["stem", "business", "healthcare", "arts", "law_public_service"],
      college_fit: ["ivy_league", "state_university", "liberal_arts", "technical_institute", "business_school"],
      learning_style: ["analytical", "creative", "hands_on", "research", "leadership"],
      student_wellness: ["exam_stress", "academic_burnout", "social_adaptability", "motivation"]
    }
  },
  military: {
    label: "🎖️ Military Readiness",
    flow: "readiness",
    types: {
      combat: ["infantry", "tactical_support", "special_operations"],
      intelligence: ["intelligence_analyst", "surveillance", "cyber_intelligence"],
      logistics: ["supply_chain", "transportation", "field_operations"],
      technical: ["cyber", "engineering", "communications"],
      medical: ["combat_medic", "military_healthcare", "mental_health_support"],
      leadership: ["officer_track", "squad_leader", "mission_readiness"]
    }
  },
  healthcare: {
    label: "🏥 Healthcare Clinical Fit",
    flow: "clinical_fit",
    types: {
      patient_care: ["nursing", "patient_coordinator", "emergency_department", "icu"],
      clinical_operations: ["surgery", "diagnostics", "laboratory", "pharmacy"],
      mental_health: ["psychiatry", "behavioral_health", "counseling"],
      administration: ["billing", "compliance", "hospital_operations", "health_informatics"],
      healthcare_leadership: ["shift_lead", "clinical_manager", "care_coordination"]
    }
  },
  government: {
    label: "🏛️ Government Public Service",
    flow: "public_service",
    types: {
      public_administration: ["admin_services", "policy_support", "public_operations"],
      compliance: ["audit", "investigation", "regulatory_review"],
      public_facing: ["dmv", "citizen_services", "social_services"],
      emergency: ["emergency_management", "disaster_response", "public_safety_support"],
      technology: ["government_it", "cybersecurity", "data_systems"]
    }
  },
  executive: {
    label: "👑 Executive Leadership",
    flow: "leadership",
    types: {
      executive_assessment: ["ceo_readiness", "vp", "director", "founder"],
      leadership: ["crisis_management", "strategic_decision_making", "conflict_management", "team_influence"],
      performance: ["cognitive_endurance", "burnout_prevention", "recovery_optimization"],
      succession: ["future_leader", "high_potential", "leadership_pipeline"]
    }
  },
  custom: {
    label: "✨ Custom",
    flow: "custom",
    types: {}
  }
};

const formatLabel = (value) =>
  value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const TeamFormModal = ({
  show,
  onClose,
  onSubmit,
  loading,
  teamToEdit,
  mode = "create"
}) => {
  const getDefaultType = (environment) => {
    const types = Object.keys(TEAM_TAXONOMY[environment]?.types || {});
    return types[0] || "custom";
  };

  const getDefaultSubtype = (environment, teamType) => {
    const subtypes = TEAM_TAXONOMY[environment]?.types?.[teamType] || [];
    return subtypes[0] || "";
  };

  const [formData, setFormData] = useState({
    team_name: "",
    description: "",
    environment: "office",
    team_type: "general_office",
    team_subtype: "admin",
    assessment_flow: "workforce",
    custom_type: ""
  });

useEffect(() => {
  if (teamToEdit) {
    console.log("EDIT TEAM DATA:", teamToEdit);

    setFormData({
      team_name: teamToEdit.team_name || "",
      description: teamToEdit.description || "",
      environment: teamToEdit.environment || "office",
      team_type: teamToEdit.team_type || "",
      team_subtype: teamToEdit.team_subtype || "",
      assessment_flow:
        teamToEdit.assessment_flow ||
        TEAM_TAXONOMY[teamToEdit.environment]?.flow ||
        "workforce",
      custom_type: teamToEdit.custom_type || ""
    });
  } else {
    setFormData({
      team_name: "",
      description: "",
      environment: "office",
      team_type: "general_office",
      team_subtype: "admin",
      assessment_flow: "workforce",
      custom_type: ""
    });
  }
}, [teamToEdit]);

  const currentEnvironment = TEAM_TAXONOMY[formData.environment];
  const teamTypes = Object.keys(currentEnvironment?.types || {});
  const teamSubtypes = currentEnvironment?.types?.[formData.team_type] || [];

  const handleEnvironmentChange = (e) => {
    const environment = e.target.value;
    const defaultType = getDefaultType(environment);
    const defaultSubtype = getDefaultSubtype(environment, defaultType);

    setFormData((prev) => ({
      ...prev,
      environment,
      team_type: defaultType,
      team_subtype: defaultSubtype,
      assessment_flow: TEAM_TAXONOMY[environment]?.flow || "custom",
      custom_type: environment === "custom" ? prev.custom_type : ""
    }));
  };

  const handleTeamTypeChange = (e) => {
    const team_type = e.target.value;
    const defaultSubtype = getDefaultSubtype(formData.environment, team_type);

    setFormData((prev) => ({
      ...prev,
      team_type,
      team_subtype: defaultSubtype
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.team_name.trim()) {
      alert("Please enter team name");
      return;
    }

    if (formData.environment === "custom" && !formData.custom_type.trim()) {
      alert("Please enter custom team type name");
      return;
    }

    onSubmit(formData);

    if (mode === "create") {
      setFormData({
        team_name: "",
        description: "",
        environment: "office",
        team_type: "general_office",
        team_subtype: "admin",
        assessment_flow: "workforce",
        custom_type: ""
      });
    }
  };

  if (!show) return null;

  return (
    <div className="teams-modal-overlay">
      <div className="teams-modal">
        <div className="teams-modal-header">
          <h2>{mode === "edit" ? "Edit Team" : "Create New Team"}</h2>
          <button className="teams-modal-close" onClick={onClose} type="button">
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="teams-modal-form">
          <div className="form-group">
            <label htmlFor="team_name">Team Name *</label>
            <input
              type="text"
              id="team_name"
              name="team_name"
              value={formData.team_name}
              onChange={handleChange}
              placeholder="Enter team name"
              required
              maxLength="100"
            />
          </div>

          <div className="form-group">
            <label htmlFor="environment">Environment *</label>
            <select
              id="environment"
              name="environment"
              value={formData.environment}
              onChange={handleEnvironmentChange}
              required
            >
              {Object.entries(TEAM_TAXONOMY).map(([key, config]) => (
                <option key={key} value={key}>
                  {config.label}
                </option>
              ))}
            </select>
          </div>

          {formData.environment !== "custom" && (
            <>
              <div className="form-group">
                <label htmlFor="team_type">Type *</label>
                <select
                  id="team_type"
                  name="team_type"
                  value={formData.team_type}
                  onChange={handleTeamTypeChange}
                  required
                >
                  {teamTypes.map((type) => (
                    <option key={type} value={type}>
                      {formatLabel(type)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="team_subtype">Subtype *</label>
                <select
                  id="team_subtype"
                  name="team_subtype"
                  value={formData.team_subtype}
                  onChange={handleChange}
                  required
                >
                  {teamSubtypes.map((subtype) => (
                    <option key={subtype} value={subtype}>
                      {formatLabel(subtype)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Assessment Flow</label>
                <input
                  type="text"
                  value={formatLabel(formData.assessment_flow)}
                  disabled
                />
              </div>
            </>
          )}

          {formData.environment === "custom" && (
            <div className="form-group custom-type-group">
              <label htmlFor="custom_type">Custom Team Type Name *</label>
              <input
                type="text"
                id="custom_type"
                name="custom_type"
                value={formData.custom_type}
                onChange={handleChange}
                placeholder="Enter custom team type"
                required
                maxLength="50"
                autoFocus
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter team description optional"
              rows="4"
              maxLength="500"
            />
          </div>

          <div className="teams-modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading
                ? mode === "edit"
                  ? "Updating..."
                  : "Creating..."
                : mode === "edit"
                ? "Update Team"
                : "Create Team"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeamFormModal;